import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate, Outlet } from 'react-router-dom';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { toast } from 'sonner';
import { 
  Shield, LayoutDashboard, Users, Building2, CreditCard, 
  Settings, BarChart3, MessageSquare, Bug, LogOut,
  Menu, X, ChevronRight, RefreshCw
} from 'lucide-react';

const AdminLayout = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [adminKey, setAdminKey] = useState('');
  const [loading, setLoading] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(true);

  const API = process.env.REACT_APP_BACKEND_URL;

  // Check if admin key is stored in session
  useEffect(() => {
    const storedKey = sessionStorage.getItem('aura_admin_key');
    if (storedKey) {
      setAdminKey(storedKey);
      setIsAuthenticated(true);
    }
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const response = await fetch(`${API}/api/admin/verify`, {
        headers: { 'X-Admin-Key': adminKey }
      });
      
      if (response.ok) {
        sessionStorage.setItem('aura_admin_key', adminKey);
        setIsAuthenticated(true);
        toast.success('Admin access granted');
      } else {
        toast.error('Invalid admin key');
      }
    } catch (error) {
      toast.error('Failed to verify admin key');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem('aura_admin_key');
    setIsAuthenticated(false);
    setAdminKey('');
    navigate('/admin');
  };

  const navItems = [
    { path: '/admin', icon: LayoutDashboard, label: 'Overview', exact: true },
    { path: '/admin/users', icon: Users, label: 'Users' },
    { path: '/admin/organizations', icon: Building2, label: 'Organizations' },
    { path: '/admin/analytics', icon: BarChart3, label: 'Analytics' },
    { path: '/admin/revenue', icon: CreditCard, label: 'Revenue' },
    { path: '/admin/chat-analytics', icon: MessageSquare, label: 'Chat Insights' },
    { path: '/admin/support-tickets', icon: MessageSquare, label: 'Support Tickets' },
    { path: '/admin/issues', icon: Bug, label: 'Issues' },
    { path: '/admin/settings', icon: Settings, label: 'Settings' },
  ];

  const isActive = (item) => {
    if (item.exact) {
      return location.pathname === item.path;
    }
    return location.pathname.startsWith(item.path);
  };

  // Login Screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-aura-bg flex items-center justify-center p-4">
        <Card className="w-full max-w-md bg-aura-surface border-white/10">
          <CardHeader className="text-center">
            <div className="w-16 h-16 mx-auto rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center mb-4">
              <Shield className="w-8 h-8 text-white" />
            </div>
            <CardTitle className="text-2xl font-heading text-white uppercase tracking-tight">
              Admin Console
            </CardTitle>
            <p className="text-aura-text-muted text-sm mt-2">
              Aura Back Office - Authorized Personnel Only
            </p>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4">
              <div className="space-y-2">
                <label className="text-xs uppercase tracking-wider text-aura-text-subtle">Admin Key</label>
                <Input
                  type="password"
                  value={adminKey}
                  onChange={(e) => setAdminKey(e.target.value)}
                  placeholder="Enter admin secret key"
                  className="h-12 bg-aura-bg border-white/10 text-white"
                  data-testid="admin-key-input"
                />
              </div>
              <Button
                type="submit"
                disabled={loading || !adminKey}
                className="w-full h-12 bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white font-heading uppercase tracking-wider"
                data-testid="admin-login-btn"
              >
                {loading ? (
                  <RefreshCw className="w-5 h-5 animate-spin" />
                ) : (
                  'Access Console'
                )}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-aura-bg flex">
      {/* Sidebar */}
      <aside className={`${sidebarOpen ? 'w-64' : 'w-20'} bg-aura-surface border-r border-white/10 flex flex-col transition-all duration-300`}>
        {/* Logo */}
        <div className="p-4 border-b border-white/10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center flex-shrink-0">
              <Shield className="w-5 h-5 text-white" />
            </div>
            {sidebarOpen && (
              <div>
                <h1 className="font-heading font-bold text-white uppercase tracking-tight text-sm">Admin</h1>
                <p className="text-xs text-aura-text-muted">Back Office</p>
              </div>
            )}
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-4 space-y-1">
          {navItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all ${
                isActive(item)
                  ? 'bg-amber-500/20 text-amber-400'
                  : 'text-aura-text-muted hover:text-white hover:bg-white/5'
              }`}
            >
              <item.icon className="w-5 h-5 flex-shrink-0" />
              {sidebarOpen && <span className="text-sm font-medium">{item.label}</span>}
            </Link>
          ))}
        </nav>

        {/* Footer */}
        <div className="p-4 border-t border-white/10 space-y-2">
          <Button
            variant="ghost"
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="w-full justify-start text-aura-text-muted hover:text-white"
          >
            {sidebarOpen ? <X className="w-5 h-5 mr-2" /> : <Menu className="w-5 h-5" />}
            {sidebarOpen && 'Collapse'}
          </Button>
          <Button
            variant="ghost"
            onClick={handleLogout}
            className="w-full justify-start text-red-400 hover:text-red-300 hover:bg-red-500/10"
          >
            <LogOut className="w-5 h-5 flex-shrink-0" />
            {sidebarOpen && <span className="ml-2">Logout</span>}
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-auto">
        <div className="p-6">
          {children}
        </div>
      </main>
    </div>
  );
};

export default AdminLayout;
